// ============================================================================
// FSA-2.2 — class subtotal formula helpers
// ============================================================================
// Shared helpers for evaluating a template-declared `subtotal_formula`
// across a class's line items. Used by:
//
//   - FsaNoteModal — class amount_actual / amount_rounded computation
//     in setLineItems + recalculateAllTotals (the auditor's edit paths).
//   - MainNotePane — line-item-vs-class-total reconciliation drift
//     check (the chip that says "drift X" next to a class total when
//     line items don't sum to it).
//   - Backend mirror: EngagementNoteDataService.EvaluateSubtotal — same
//     parsing rules, same operand-matching convention. Keep in sync
//     when extending the formula grammar (e.g. multi-operand linear).
//
// Current supported formula shape: "<OP_A> - <OP_B>" (two-operand
// subtraction). Operand names match line-item `local_code` from the
// template's `default_line_items`; materialised line items use
// `local_id = default::<CODE>` so the formula can find them by name
// across rename / reorder.

import type { NoteLineItem } from 'api/types/fsa/noteData.types';

const DEFAULT_LINE_ITEM_PREFIX = 'default::';

export const defaultLineItemLocalId = (localCode: string): string =>
  `${DEFAULT_LINE_ITEM_PREFIX}${localCode.trim().toUpperCase()}`;

const findFormulaOperand = (
  lineItems: NoteLineItem[],
  code: string,
): NoteLineItem | undefined => {
  const target = defaultLineItemLocalId(code);
  return lineItems.find((li) => li.local_id === target || li.local_id === code);
};

export type SubtotalFormulaField =
  | 'amount_actual'
  | 'amount_rounded'
  | 'prior_amount_actual'
  | 'prior_amount_rounded';

/**
 * Evaluate the class subtotal across line items honouring a template
 * `subtotal_formula`. Falls back to `Σ field` over all line items when
 * the formula is empty / null / not parseable.
 *
 * Rounds to 2dp to wash out IEEE 754 noise — `2731.79 - 1737.19`
 * evaluates to `994.5999999999999` in JS, but the auditor expects
 * `994.60`. Currency values are 2dp by convention and operand line
 * items are auditor-typed at 2dp, so the result is mathematically
 * 2dp; the rounding just normalises the bits.
 *
 * The Σ fallback is NOT rounded (preserves the existing legacy
 * behaviour for every non-formula class).
 */
export function evalClassSubtotal(
  lineItems: NoteLineItem[],
  field: SubtotalFormulaField,
  formula?: string | null,
): number {
  const f = formula?.trim();
  if (f) {
    const parts = f.split('-').map((s) => s.trim()).filter(Boolean);
    if (parts.length === 2) {
      const [aCode, bCode] = parts;
      const a = findFormulaOperand(lineItems, aCode);
      const b = findFormulaOperand(lineItems, bCode);
      const raw =
        ((a?.[field] as number | undefined) ?? 0) -
        ((b?.[field] as number | undefined) ?? 0);
      return Math.round((raw + Number.EPSILON) * 100) / 100;
    }
    // Unknown formula shape → fall through to Σ.
  }
  return lineItems.reduce((s, li) => s + ((li[field] as number | undefined) ?? 0), 0);
}
